import type { StaffMember, ServiceTemplate, ServiceCategory, Ticket, TicketService, Patient, Nota } from '@/lib/types';

// ── Nota column definitions (shared with database.ts) ──

export const HC_COLUMNS = [
  'historia', 'antecedentes_heredo', 'antecedentes_no_patologicos', 'antecedentes_patologicos',
  'antecedentes_gineco', 'padecimiento_actual', 'signos_sintomas', 'cardiovascular', 'respiratorio',
  'digestivo', 'urogenital', 'endocrino', 'hematopoyetico', 'nervioso', 'musculo_esqueletico',
  'organos_sentidos', 'psiquica', 'piel_tegumentos', 'habitus', 'cabeza', 'cuello', 'torax',
  'abdomen', 'extremidades', 'genitales', 'resumen_exploracion', 'resultados_lab', 'receta', 'plan_nota',
] as const;

export const EVOL_COLUMNS = [
  'evolucion', 'exploracion_fisica', 'diagnostico', 'plan_tratamiento',
  'peso_talla', 'signos_vitales', 'pronostico', 'recomendado_por',
] as const;

export const ALL_NOTA_COLUMNS = [...EVOL_COLUMNS, ...HC_COLUMNS];

// ── Staff ──

export function mapStaffRow(r: any): StaffMember {
  return {
    id: r.id,
    record_id: r.id,
    name: r.name,
    role: r.role,
    title: r.title ?? null,
    initials: r.initials,
    username: r.username,
    password: r.password,
    color: r.color,
    isAdmin: r.is_admin,
    system_role: r.system_role || (r.is_admin ? 'admin' : 'staff'),
    avatar_url: r.avatar_url || '',
    isMedical: r.is_medical ?? true,
    active: r.active ?? true,
    services: r.services || [],
    email: r.email || undefined,
    commission_pct: r.commission_pct ?? null,
    vacation_days_annual: r.vacation_days_annual ?? 0,
    sick_days_annual: r.sick_days_annual ?? 0,
    // null preserved as-is so `derivePermissions()` knows to fall back
    // to the system_role default. Pre-migration rows (no column yet)
    // also land here as `undefined` → null.
    permissions: r.permissions ?? null,
    weekly_schedule: r.weekly_schedule ?? null,
    availability_dates: r.availability_dates ?? [],
    last_seen_at: r.last_seen_at ?? null,
  };
}

// ── Categories ──

export function mapCategoryRow(r: any): ServiceCategory {
  return {
    id: r.id,
    record_id: r.id,
    name: r.name,
    order: r.sort_order,
  };
}

// ── Services ──

export function mapServiceRow(r: any): ServiceTemplate {
  const addons = r.addons || [];
  const packages = r.packages || [];
  // Zonas projection — Phase 1 of the zonas-centric refactor.
  // Prefers the migrated `services.zonas` column; falls back to
  // deriving it from `areas` on the fly for any row the migration
  // hasn't touched yet. Default rule: every addon is available on
  // every zona, mirroring the current behavior where the addon menu
  // isn't zone-restricted. Package cross-refs come from the legacy
  // package.area_indices (positional → derived per zona at read time).
  const zonas = Array.isArray(r.zonas) && r.zonas.length > 0
    ? r.zonas
    : deriveZonasFromAreas(r.areas || [], addons, packages);
  // Legacy `areas` projection. Prod dropped `services.areas`, so when the
  // column is absent we project from `zonas` — order matches because the
  // Phase 1 migration built `zonas` from `areas` positionally, and any
  // subsequent Builder write goes through deriveLegacyFromZonas. Zona is a
  // structural superset of ServiceArea (extra cross-ref fields are
  // harmless to the ticket flow, which only reads name/price/id/materials).
  const areas = Array.isArray(r.areas) && r.areas.length > 0 ? r.areas : zonas;
  return {
    id: r.id,
    record_id: r.id,
    name: r.name,
    category: r.category,
    pricing_type: r.pricing_type,
    base_price: Number(r.base_price),
    unit_label: r.unit_label,
    min_units: r.min_units ? Number(r.min_units) : undefined,
    max_units: r.max_units ? Number(r.max_units) : undefined,
    unit_step: r.unit_step ? Number(r.unit_step) : undefined,
    tiers: r.tiers || [],
    brands: r.brands || [],
    has_areas: r.has_areas || false,
    areas,
    zonas,
    has_addons: r.has_addons || false,
    addons,
    has_package: r.is_package || false,
    packages,
    package_discount_pct: r.package_discount_pct != null ? Number(r.package_discount_pct) : undefined,
    staff_ids: r.staff_ids || [],
    materials: r.materials || [],
    color: r.color ?? null,
    icon: r.icon ?? null,
    description: r.description ?? null,
    image_url: r.image_url ?? null,
    duration_minutes: r.duration_minutes != null ? Number(r.duration_minutes) : null,
    active: r.active,
    notes: r.notes || undefined,
    // Tratamientos (3k) columns — migration 20260427. Without these
    // the v2 builder's writes appear to vanish on reload (R-SCHEMA-1
    // from the cutover audit).
    sku: r.sku ?? null,
    md_only: r.md_only === true,
    pre_care: r.pre_care ?? null,
    post_care: r.post_care ?? null,
    // Promo pricing — migration 20260512. Cast numeric -> number so
    // downstream price math doesn't accidentally string-concat.
    promo_price: r.promo_price != null ? Number(r.promo_price) : null,
    promo_starts_at: r.promo_starts_at ?? null,
    promo_ends_at: r.promo_ends_at ?? null,
  };
}

/** Wire projection for /api/data: drop `areas` when it is the same array
 *  `zonas` already carries (mapServiceRow aliases it when the legacy column
 *  is absent — prod dropped `services.areas`, so that is every row). A row
 *  that still carries a distinct legacy `areas` keeps it. Pair with
 *  aliasServiceAreas on the client; the ServiceTemplate contract is unchanged
 *  end to end. */
export function stripServiceAreasForWire<T extends { areas?: unknown; zonas?: unknown }>(services: T[]): T[] {
  return services.map((s) => {
    if (s.areas === undefined || s.areas !== s.zonas) return s;
    const { areas: _dropped, ...rest } = s;
    void _dropped;
    return rest as T;
  });
}

/** Client half of stripServiceAreasForWire: a service that arrived without
 *  `areas` reads them from `zonas` (structural superset of ServiceArea). */
export function aliasServiceAreas<T extends { areas?: unknown; zonas?: unknown }>(services: T[]): T[] {
  return services.map((s) => (s.areas === undefined && Array.isArray(s.zonas) ? { ...s, areas: s.zonas } : s));
}

/** Phase 1 fallback for rows that haven't been migrated yet. Builds a
 *  Zona[] from the legacy `areas` array by attaching addon_ids (all
 *  addons available on every zona) + package_ids (translated from
 *  package.area_indices on the fly). Pure function — no IO. */
function deriveZonasFromAreas(
  areas: any[],
  addons: any[],
  packages: any[],
): any[] {
  if (!Array.isArray(areas) || areas.length === 0) return [];
  const allAddonIds = (addons || [])
    .map((a) => (a && typeof a.id === 'string' ? a.id : null))
    .filter((x): x is string => !!x);
  return areas.map((area, idx) => {
    const pkgIdsForArea = (packages || [])
      .filter(
        (p) =>
          p &&
          typeof p.id === 'string' &&
          Array.isArray(p.area_indices) &&
          p.area_indices.includes(idx),
      )
      .map((p) => p.id);
    return {
      ...area,
      addon_ids: allAddonIds,
      package_ids: pkgIdsForArea,
    };
  });
}

// ── Patients ──

const choiceSource = (v: unknown): 'forma' | 'clinica' | 'staff' | null =>
  v === 'forma' || v === 'clinica' || v === 'staff' ? v : null;

export function mapPatientRow(r: any): Patient {
  return {
    id: r.id,
    record_id: r.id,
    name: r.name == null ? '' : String(r.name),
    phone: r.phone == null ? undefined : String(r.phone),
    email: r.email || undefined,
    notes: r.notes || undefined,
    date_of_birth: r.date_of_birth || undefined,
    sex: r.sex || undefined,
    address: r.address || undefined,
    // expediente is an integer column in Postgres, so r.expediente
    // arrives as a number. Patient.expediente is typed string and the
    // pacientes search filter does `.toLowerCase()` on it — coerce
    // here so the contract holds and consumers don't crash.
    expediente: r.expediente == null ? undefined : String(r.expediente),
    // Defaults to false when the column is absent (e.g. an RPC projection that
    // doesn't select it) — the Carpeta button stays hidden unless explicitly true.
    dropbox_folder_ok: r.dropbox_folder_ok === true,
    has_whatsapp: r.has_whatsapp || false,
    whatsapp_status: (() => {
      const ws = r.whatsapp_status;
      if (ws === 'yes' || ws === 'si' || ws === 'sí') return 'yes';
      if (ws === 'pending' || ws === 'pendiente') return 'pending';
      if (r.has_whatsapp) return 'yes';
      return 'pending';
    })(),
    referral_source: r.referral_source || undefined,
    status: r.status || (r.active === false ? 'inactivo' : 'activo'),
    patient_type: r.patient_type || [],
    active: r.status ? r.status !== 'inactivo' : (r.active ?? true),
    created_at: r.created_at || undefined,
    sentiment: r.sentiment === 'contento' || r.sentiment === 'neutral' || r.sentiment === 'molesto' ? r.sentiment : null,
    sentiment_source: r.sentiment_source === 'ai' || r.sentiment_source === 'manual' ? r.sentiment_source : null,
    optional_purposes_refused_at: r.optional_purposes_refused_at ?? null,
    optional_purposes_refused_source: choiceSource(r.optional_purposes_refused_source),
    follow_up_stopped_at: r.follow_up_stopped_at ?? null,
    follow_up_stopped_source: choiceSource(r.follow_up_stopped_source),
    transfers_refused_at: r.transfers_refused_at ?? null,
    transfers_refused_source: choiceSource(r.transfers_refused_source),
  };
}

// ── Notas ──

export function mapNotaRow(r: any): Nota {
  const nota: any = {
    id: r.id,
    record_id: r.id,
    type: r.type,
    patient_id: r.patient_id,
    patient_name: r.patient_name || '',
    staff_id: r.staff_id,
    staff_name: r.staff_name || '',
    content: r.content || '',
    status: r.status,
    ticket_id: r.ticket_id,
    created_at: r.created_at,
    // Y6 from cutover audit — without this the /notas editor's
    // "Editada hace N min" stays blank and the client-side stale filter
    // falls back to created_at, over-reporting stale rows.
    updated_at: r.updated_at,
    content_updated_at: r.content_updated_at ?? null,
    // Revisión state — null until staff explicitly marks the nota as
    // revisada. Restored from the R-24 deferral once the column landed.
    revisada_at: r.revisada_at || null,
    revisada_by: r.revisada_by || null,
    revisada_by_name: r.revisada_by_name || null,
  };
  for (const k of ALL_NOTA_COLUMNS) nota[k] = r[k] || '';
  return nota;
}

// ── Ticket Services ──

export function mapTicketServiceRow(s: any): TicketService {
  return {
    service_id: s.service_id,
    name: s.name,
    price: Number(s.price),
    category: s.category || '',
    units: s.units ? Number(s.units) : undefined,
    unit_label: s.unit_label || undefined,
    tier_label: s.tier_label || undefined,
    is_package: s.is_package || false,
    package_sessions: s.package_sessions || undefined,
    redemption_package_id: s.redemption_package_id || undefined,
    material_cost: s.material_cost ? Number(s.material_cost) : undefined,
    pending_package_redemption: s.pending_package_redemption || false,
    staff: s.staff || undefined,
    zone_snapshot: s.zone_snapshot ?? null,
    duration_minutes_snapshot:
      s.duration_minutes_snapshot != null ? Number(s.duration_minutes_snapshot) : null,
    // Migration 20260512 — populated only when a promo was applied.
    original_price: s.original_price != null ? Number(s.original_price) : null,
    // Phase 2.7 (migration 20260513000001) — snapshot of what the staff
    // actually picked. Used by /reportes consumption variance to count
    // per-zona + per-add-on materials.
    selected_zona_id: s.selected_zona_id ?? null,
    selected_addon_ids: Array.isArray(s.selected_addon_ids) ? s.selected_addon_ids : null,
  };
}

/** Lift a PostgREST-embedded `ticket_service_staff(staff_id, role,
 *  staff:staff_id(name))` array onto each raw ticket_services row as the
 *  `staff` array mapTicketServiceRow reads — the exact shape the separate
 *  attachTicketServiceStaff round trip used to produce (set only when there
 *  are rows; `staff_name` '' when the join is missing; role defaults to
 *  'primary'). Mutates the raw rows in place, like the round-trip helper did,
 *  and removes the embed key so the raw shape matches the old path. */
export function liftEmbeddedServiceStaff(tickets: any[]): void {
  for (const tk of tickets || []) {
    for (const svc of (tk?.ticket_services || [])) {
      if (!svc || !('ticket_service_staff' in svc)) continue;
      const rows: any[] = Array.isArray(svc.ticket_service_staff) ? svc.ticket_service_staff : [];
      delete svc.ticket_service_staff;
      if (rows.length === 0) continue;
      svc.staff = rows.map((row) => ({
        staff_id: row.staff_id,
        staff_name: row.staff?.name || '',
        role: row.role || 'primary',
      }));
    }
  }
}

// ── Tickets ──

export function mapTicketRow(r: any, services: TicketService[]): Ticket {
  return {
    id: r.id,
    record_id: r.id,
    ticket_number: r.ticket_number,
    client: r.client,
    patient_id: r.patient_id,
    status: r.status,
    services,
    discount: Number(r.discount) || 0,
    discount_note: r.discount_note || '',
    propina: r.propina != null ? Number(r.propina) : undefined,
    propina_staff_id: r.propina_staff_id || undefined,
    propina_method: r.propina_method || undefined,
    total: Number(r.total) || 0,
    payment_method: r.payment_method,
    payment_detail: r.payment_detail,
    created_at: r.created_at,
    created_by: r.created_by,
    closed_at: r.closed_at,
    closed_by: r.closed_by || undefined,
    cancelled_at: r.cancelled_at || undefined,
    cancelled_by: r.cancelled_by || undefined,
    invoiced: r.invoiced || false,
    invoiced_by: r.invoiced_by || '',
    recibo_url: r.recibo_url || undefined,
    voucher_url: r.voucher_url || undefined,
  };
}
